import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Spinner, Alert, Badge } from 'react-bootstrap';
import { FaSave, FaArrowLeft, FaTimes, FaPlus } from 'react-icons/fa';
import PageHeader from '../components/PageHeader';
import productService from '../services/productService';
import categoryService from '../services/categoryService';
import vendorService from '../services/vendorService';

function ProductEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [categoryError, setCategoryError] = useState('');
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [showCategorySelector, setShowCategorySelector] = useState(false);
  const [tempSelectedParent, setTempSelectedParent] = useState(null);
  const [tempSelectedChild, setTempSelectedChild] = useState(null);
  const [tempAvailableChildren, setTempAvailableChildren] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    category_ids: [],
    vendor_id: '',
    image: '',
    product_type: 'deal',
    is_discount: false,
    is_purchasable: true,
    valid_from: '',
    valid_until: '',
    max_redeems: '',
    current_redeems: 0,
    current_redeems: 0,
    status: 'published',
    discount: 0,
    media: {}
  });

  const STORAGE_URL = process.env.REACT_APP_STORAGE_URL || 'http://localhost:5000/static/';

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    return `${STORAGE_URL}${imagePath}`;
  };
  const [slug, setSlug] = useState('');
  const fetchProduct = useCallback(async () => {
    if (!id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const data = await productService.getById(id);

      // Parse media if it's a JSON string
      let parsedMedia = {};

      if (data.media) {
        parsedMedia = typeof data.media === 'string' ? JSON.parse(data.media) : data.media;
      }
      if (data.slug) {
        setSlug(data.slug);
      }

      setFormData({
        title: data.title || '',
        description: data.description || '',
        price: data.price || '',
        category_ids: data.category_ids || [],
        vendor_id: data.vendor_id || '',
        image: data.image || '',
        product_type: data.product_type || 'deal',
        is_discount: data.is_discount !== undefined ? data.is_discount : false,
        is_purchasable: data.is_purchasable !== undefined ? data.is_purchasable : true,
        valid_from: data.valid_from ? data.valid_from.split('T')[0] : '',
        valid_until: data.valid_until ? data.valid_until.split('T')[0] : '',
        max_redeems: data.max_redeems || '',
        current_redeems: data.current_redeems || 0,
        max_redeems: data.max_redeems || '',
        current_redeems: data.current_redeems || 0,
        status: data.status || 'published',
        discount: data.discount || 0,
        media: parsedMedia
      });
    } catch (err) {
      setError('Failed to load product');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchCategories = useCallback(async () => {
    try {
      const data = await categoryService.getAll({ paginate: 'false' });
      setCategories(Array.isArray(data) ? data : data.categories || []);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  }, []);

  // Update temp available children when temp selected parent changes
  useEffect(() => {
    if (tempSelectedParent) {
      const children = categories.filter(cat => cat.parent_id === tempSelectedParent.id);
      setTempAvailableChildren(children);
      setTempSelectedChild(null); // Reset child selection when parent changes
    } else {
      setTempAvailableChildren([]);
      setTempSelectedChild(null);
    }
  }, [tempSelectedParent, categories]);

  const fetchVendors = useCallback(async () => {
    try {
      // Use the select endpoint which returns a flat vendors list for dropdowns
      const data = await vendorService.getForSelect({ per_page: 1000 });
      setVendors(Array.isArray(data) ? data : data.vendors || []);
    } catch (err) {
      console.error('Failed to load vendors for select:', err);
    }
  }, []);

  useEffect(() => {
    fetchProduct();
    fetchCategories();
    fetchVendors();
  }, [fetchProduct, fetchCategories, fetchVendors]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAddCategory = () => {
    // Determine which category to add
    const categoryToAdd = tempAvailableChildren.length > 0 && tempSelectedChild
      ? tempSelectedChild
      : tempSelectedParent;

    if (!categoryToAdd) {
      setCategoryError('Please select a category');
      return;
    }

    // Check if already added
    if (formData.category_ids.includes(categoryToAdd.id)) {
      setCategoryError('Category already added');
      return;
    }

    // Add to category_ids
    setFormData({ ...formData, category_ids: [...formData.category_ids, categoryToAdd.id] });

    // Reset temporary selections and hide selector
    setTempSelectedParent(null);
    setTempSelectedChild(null);
    setTempAvailableChildren([]);
    setShowCategorySelector(false);
    setCategoryError('');
  };

  const handleRemoveCategory = (categoryId) => {
    const newCategoryIds = formData.category_ids.filter(id => id !== categoryId);

    if (newCategoryIds.length === 0) {
      setCategoryError('Cannot remove the last category. Please add another category first.');
      return;
    }

    setFormData({ ...formData, category_ids: newCategoryIds });
    setCategoryError('');
  };

  const getCategoryById = (categoryId) => {
    return categories.find(cat => cat.id === categoryId);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setError('');

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);

      const data = await productService.uploadImage(formDataUpload);

      setFormData({ ...formData, image: `products/${data.filename}` });
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to upload image';
      setError(errorMessage);
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  const handleMediaUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    setError('');

    try {
      const uploadedPaths = [];

      for (const file of files) {
        const formDataUpload = new FormData();
        formDataUpload.append('image', file);

        const data = await productService.uploadImage(formDataUpload);
        uploadedPaths.push(`products/${data.filename}`);
      }

      if (uploadedPaths.length > 0) {
        const currentImages = formData.media?.images || [];
        setFormData({
          ...formData,
          media: {
            ...formData.media,
            images: [...currentImages, ...uploadedPaths]
          }
        });
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to upload media images';
      setError(errorMessage);
      console.error(err);
    } finally {
      setUploading(false);
      e.target.value = ''; // Reset file input
    }
  };

  const handleRemoveMediaImage = (index) => {
    const currentImages = formData.media?.images || [];
    const newImages = currentImages.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      media: {
        ...formData.media,
        images: newImages
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    // P1-7: Validate that at least one category is selected
    if (!formData.category_ids || formData.category_ids.length === 0) {
      setError('Please select at least one category for this product');
      setSaving(false);
      return;
    }

    try {
      // Ensure media is a proper object
      const mediaObj = formData.media || {};

      const submitData = {
        ...formData,
        price: parseFloat(formData.price) || 0,
        media: mediaObj,
        // Convert empty strings to null for optional date/number fields
        valid_from: formData.valid_from || null,
        valid_until: formData.valid_until || null,
        max_redeems: formData.max_redeems ? parseInt(formData.max_redeems) : null,
        discount: parseFloat(formData.discount) || 0
      };

      // Remove legacy digital product fields (not needed)
      delete submitData.file_path;
      delete submitData.file_type;
      delete submitData.files_included;
      delete submitData.compatible_with;

      // submitData logged during development; removed to satisfy eslint no-console rule

      if (id) {
        await productService.update(id, submitData);
      } else {
        await productService.create(submitData);
      }
      navigate('/products');
    } catch (err) {
      setError(`Failed to ${id ? 'update' : 'create'} product: ${err.response?.data?.error || err.message}`);
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <Container fluid className="px-4">
      <PageHeader
        title={
          <div className="d-flex align-items-center gap-3">
            <span>{id ? 'Edit Product' : 'Add New Product'}</span>
            {id && (
              <Badge bg={formData.status === 'published' ? 'success' : formData.status === 'draft' ? 'warning' : 'secondary'} className="fs-6 px-3 py-2">
                {formData.status}
              </Badge>
            )}
          </div>
        }
        subtitle={id ? `Editing product #${id}` : 'Create a new product'}
      />

      <Row className="mb-3">
        <Col>
          <Button variant="secondary" onClick={() => navigate('/products')}>
            <FaArrowLeft className="me-2" />
            Back to Products
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Form onSubmit={handleSubmit}>
        <Row className="g-4">
          <Col lg={8}>
            <Card className="border-0 shadow-sm mb-4">
              <Card.Body>
                <h5 className="fw-semibold mb-4">Basic Information</h5>

                <Form.Group className="mb-3">
                  <Form.Label>Product Title *</Form.Label>
                  <Form.Control
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                    placeholder="Enter product title"
                  />
                  <span className="text-muted">Slug: {slug}</span>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Enter product description"
                  />
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Price *</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        name="price"
                        value={formData.price}
                        onChange={handleChange}
                        required
                        placeholder="0.00"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Vendor</Form.Label>
                      <Form.Select
                        name="vendor_id"
                        value={formData.vendor_id}
                        onChange={handleChange}
                      >
                        <option value="">No Vendor</option>
                        {vendors.map((vendor) => (
                          <option key={vendor.id} value={vendor.id}>
                            {vendor.name}
                            {vendor.is_verified && ' ✓'}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Text className="text-muted">
                        Associate this product with a verified vendor/company
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm mb-4">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="fw-semibold mb-0">Categories *</h5>
                  {!showCategorySelector && (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => setShowCategorySelector(true)}
                    >
                      <FaPlus className="me-1" /> Add Category
                    </Button>
                  )}
                </div>

                <Form.Text className="text-muted d-block mb-3">
                  Select parent category, then subcategory if available. Click Add to include it. Products must have at least one category.
                </Form.Text>

                {categoryError && (
                  <Alert variant="warning" dismissible onClose={() => setCategoryError('')} className="mb-3">
                    {categoryError}
                  </Alert>
                )}

                {/* Display selected categories as badges */}
                <div
                  className={`mb-3 p-3 rounded ${formData.category_ids.length === 0 && error && error.includes('category') ? 'border border-danger bg-light' : ''}`}
                  style={{ minHeight: '50px' }}
                >
                  {formData.category_ids.length === 0 ? (
                    <p className={`mb-0 ${error && error.includes('category') ? 'text-danger fw-semibold' : 'text-muted'}`}>
                      No categories selected {error && error.includes('category') && '- Please select at least one'}
                    </p>
                  ) : (
                    <div className="d-flex flex-wrap gap-2">
                      {formData.category_ids.map((categoryId) => {
                        const category = getCategoryById(categoryId);
                        if (!category) {
                          // Category not loaded yet or doesn't exist - show ID as fallback
                          return (
                            <Badge
                              key={categoryId}
                              bg="secondary"
                              className="d-flex align-items-center gap-2 px-3 py-2"
                              style={{ fontSize: '0.9rem', opacity: 0.6 }}
                            >
                              Category #{categoryId}
                              <FaTimes
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleRemoveCategory(categoryId)}
                              />
                            </Badge>
                          );
                        }
                        return (
                          <Badge
                            key={categoryId}
                            bg="secondary"
                            className="d-flex align-items-center gap-2 px-3 py-2"
                            style={{ fontSize: '0.9rem' }}
                          >
                            {category.name}
                            <FaTimes
                              style={{ cursor: 'pointer' }}
                              onClick={() => handleRemoveCategory(categoryId)}
                            />
                          </Badge>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Category Selector */}
                {showCategorySelector && (
                  <div className="border rounded p-3 mt-2">
                    {categories.length === 0 ? (
                      <>
                        <p className="text-muted mb-3">No categories available</p>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            setShowCategorySelector(false);
                            setTempSelectedParent(null);
                            setTempSelectedChild(null);
                            setTempAvailableChildren([]);
                          }}
                        >
                          Close
                        </Button>
                      </>
                    ) : (
                      <>
                        <Form.Group className="mb-3">
                          <Form.Label>Parent Category</Form.Label>
                          <Form.Select
                            value={tempSelectedParent?.id || ''}
                            onChange={(e) => {
                              const parentId = parseInt(e.target.value);
                              const parentCategories = categories.filter(cat => !cat.parent_id);
                              const parent = parentCategories.find(cat => cat.id === parentId);
                              setTempSelectedParent(parent || null);
                            }}
                          >
                            <option value="">Select a parent category...</option>
                            {categories.filter(cat => !cat.parent_id).map((parent) => (
                              <option key={parent.id} value={parent.id}>
                                {parent.name}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>

                        {tempSelectedParent && tempAvailableChildren.length > 0 && (
                          <Form.Group className="mb-3">
                            <Form.Label>Subcategory</Form.Label>
                            <Form.Select
                              value={tempSelectedChild?.id || ''}
                              onChange={(e) => {
                                const childId = parseInt(e.target.value);
                                const child = tempAvailableChildren.find(cat => cat.id === childId);
                                setTempSelectedChild(child || null);
                              }}
                            >
                              <option value="">Select a subcategory...</option>
                              {tempAvailableChildren.map((child) => (
                                <option key={child.id} value={child.id}>
                                  {child.name}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        )}

                        <div className="d-flex gap-2">
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={handleAddCategory}
                            disabled={!tempSelectedParent}
                          >
                            Add
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                              setShowCategorySelector(false);
                              setTempSelectedParent(null);
                              setTempSelectedChild(null);
                              setTempAvailableChildren([]);
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm mb-4">
              <Card.Body>
                <h5 className="fw-semibold mb-4">Product Settings</h5>

                <Form.Group className="mb-3">
                  <Form.Label>Product Type</Form.Label>
                  <Form.Select
                    name="product_type"
                    value={formData.product_type}
                    onChange={(e) => {
                      const newType = e.target.value;
                      const isPurchasable = newType === 'deal';

                      // Clear offer fields when switching to 'deal'
                      const updates = {
                        product_type: newType,
                        is_purchasable: isPurchasable
                      };

                      if (newType === 'deal') {
                        updates.valid_from = '';
                        updates.valid_until = '';
                        updates.max_redeems = '';
                      }

                      setFormData({ ...formData, ...updates });
                    }}
                  >
                    <option value="deal">Deal - Purchasable product with special price</option>
                    <option value="offer">Offer - Discount code users can redeem</option>
                    <option value="limited-offer">Limited Offer - Offer with time/quantity limits</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    <strong>Deal:</strong> Product users can purchase. <strong>Offer/Limited Offer:</strong> Discount users redeem (not purchasable directly)
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="is_discount"
                    name="is_discount"
                    label="Price represents a discount"
                    checked={formData.is_discount}
                    onChange={(e) => setFormData({ ...formData, is_discount: e.target.checked })}
                  />
                  <Form.Text className="text-muted">
                    Check if the price field contains discount value (e.g., 20% off, $10 off) rather than final price
                  </Form.Text>
                </Form.Group>

                {(formData.product_type === 'offer' || formData.product_type === 'limited-offer') && (
                  <Form.Group className="mb-3">
                    <Form.Label>Discount Percentage (%)</Form.Label>
                    <Form.Control
                      type="number"
                      name="discount"
                      value={formData.discount}
                      onChange={handleChange}
                      min="0"
                      max="100"
                      step="0.1"
                      placeholder="0"
                    />
                    <Form.Text className="text-muted">
                      Enter the percentage discount (0-100%)
                    </Form.Text>
                  </Form.Group>
                )}

                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="is_purchasable"
                    name="is_purchasable"
                    label={formData.is_purchasable ? "Purchasable (Add to cart enabled)" : "Redeemable only (No cart purchase)"}
                    checked={formData.is_purchasable}
                    onChange={(e) => setFormData({ ...formData, is_purchasable: e.target.checked })}
                    disabled={formData.product_type === 'offer' || formData.product_type === 'limited-offer'}
                  />
                  <Form.Text className="text-muted">
                    {formData.product_type === 'offer' || formData.product_type === 'limited-offer'
                      ? 'Offers are redeemable only (automatically disabled for purchase)'
                      : 'Toggle between purchasable product or redemption-only'}
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                  >
                    <option value="draft">Draft - Not visible to customers</option>
                    <option value="published">Published - Visible to customers</option>
                    <option value="archived">Archived - Hidden from listings</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Control product visibility and availability
                  </Form.Text>
                </Form.Group>

                {(formData.product_type === 'offer' || formData.product_type === 'limited-offer') && (
                  <>
                    <hr className="my-4" />
                    <h6 className="fw-semibold mb-3">{formData.product_type === 'limited-offer' ? 'Limited Offer Settings' : 'Offer Settings'}</h6>

                    {formData.product_type === 'limited-offer' && (
                      <Row className="g-3">
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Valid From</Form.Label>
                            <Form.Control
                              type="date"
                              name="valid_from"
                              value={formData.valid_from}
                              onChange={handleChange}
                            />
                            <Form.Text className="text-muted">
                              Offer start date (optional)
                            </Form.Text>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Valid Until</Form.Label>
                            <Form.Control
                              type="date"
                              name="valid_until"
                              value={formData.valid_until}
                              onChange={handleChange}
                            />
                            <Form.Text className="text-muted">
                              Offer expiration date (optional)
                            </Form.Text>
                          </Form.Group>
                        </Col>
                      </Row>
                    )}

                    <Row className="g-3">
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Maximum Redeems</Form.Label>
                          <Form.Control
                            type="number"
                            name="max_redeems"
                            value={formData.max_redeems}
                            onChange={handleChange}
                            min="0"
                            placeholder="Unlimited"
                          />
                          <Form.Text className="text-muted">
                            {formData.product_type === 'limited-offer'
                              ? 'Required for limited offers (leave empty for unlimited)'
                              : 'Optional - leave empty for unlimited redemptions'}
                          </Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Current Redeems</Form.Label>
                          <Form.Control
                            type="number"
                            value={formData.current_redeems || 0}
                            disabled
                            readOnly
                          />
                          <Form.Text className="text-muted">
                            Number of times this offer has been redeemed
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>
                  </>
                )}

                {/* P1-10: Redemption tracking for limited offers */}
                {id && formData.product_type === 'limited-offer' && formData.max_redeems && (
                  <Alert variant="info" className="mt-3">
                    <strong>Redemptions:</strong> {formData.current_redeems || 0} of {formData.max_redeems} used
                    {formData.max_redeems > 0 && (
                      <span className="ms-2">
                        ({Math.round(((formData.current_redeems || 0) / formData.max_redeems) * 100)}% claimed)
                      </span>
                    )}
                  </Alert>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col lg={4}>



            <Card className="border-0 shadow-sm">
              <Card.Body>
                <div className="d-grid gap-2">
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Saving...
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" />
                        {id ? 'Update Product' : 'Create Product'}
                      </>
                    )}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => navigate('/products')}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                </div>
              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm mt-4">
              <Card.Body>
                <h5 className="fw-semibold mb-4">Product Image</h5>

                <Form.Group className="mb-3">
                  <Form.Label>Upload Image</Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploading}
                  />
                  <Form.Text className="text-muted">
                    Upload a product image (JPG, PNG, GIF)
                  </Form.Text>
                </Form.Group>

                <div className="text-center text-muted mb-3">or</div>

                <Form.Group className="mb-3">
                  <Form.Label>Image URL</Form.Label>
                  <Form.Control
                    type="text"
                    name="image"
                    value={formData.image}
                    onChange={handleChange}
                    placeholder="Image path or URL"
                    disabled={uploading}
                  />
                  <Form.Text className="text-muted">
                    Enter relative path or full URL
                  </Form.Text>
                </Form.Group>

                {uploading && (
                  <div className="text-center mb-3">
                    <Spinner animation="border" size="sm" variant="primary" />
                    <span className="ms-2 small text-muted">Uploading...</span>
                  </div>
                )}

                {formData.image && !uploading && (
                  <div className="mt-3">
                    <p className="small text-muted mb-2">Preview:</p>
                    <img
                      src={getImageUrl(formData.image)}
                      alt="Product preview"
                      className="img-fluid rounded"
                      style={{ maxHeight: '450px', objectFit: 'cover' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm mt-4">
              <Card.Body>
                <h5 className="fw-semibold mb-4">Media Gallery</h5>

                <Form.Group className="mb-3">
                  <Form.Label>Upload Multiple Images</Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleMediaUpload}
                    disabled={uploading}
                  />
                  <Form.Text className="text-muted">
                    Upload multiple product images (JPG, PNG, GIF)
                  </Form.Text>
                </Form.Group>

                {uploading && (
                  <div className="text-center mb-3">
                    <Spinner animation="border" size="sm" variant="primary" />
                    <span className="ms-2 small text-muted">Uploading...</span>
                  </div>
                )}

                {formData.media?.images && formData.media.images.length > 0 && (
                  <div className="mt-3">
                    <p className="small text-muted mb-2">Gallery Images ({formData.media.images.length}):</p>
                    <Row className="g-2">
                      {formData.media.images.map((imagePath, index) => (
                        <Col xs={6} key={index}>
                          <div className="position-relative">
                            <img
                              src={getImageUrl(imagePath)}
                              alt={`Gallery ${index + 1}`}
                              className="img-fluid rounded"
                              style={{ width: '100%', height: '120px', objectFit: 'cover' }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                            <Button
                              variant="danger"
                              size="sm"
                              className="position-absolute top-0 end-0 m-1"
                              style={{ padding: '2px 6px' }}
                              onClick={() => handleRemoveMediaImage(index)}
                            >
                              <FaTimes size={12} />
                            </Button>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </div>
                )}

                {(!formData.media?.images || formData.media.images.length === 0) && !uploading && (
                  <p className="text-muted text-center mb-0 small">No media images uploaded yet</p>
                )}
              </Card.Body>
            </Card>

          </Col>
        </Row>
      </Form>
    </Container>
  );
}

export default ProductEditPage;
